import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Popover,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import SaveIcon from '@mui/icons-material/Save';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewComfyIcon from '@mui/icons-material/ViewComfy';

import TopBar from '../components/common/TopBar';
import usePaginatedDocumentsManifest from '../components/hooks/usePaginatedDocumentsManifest';
import { useDocumentAllPages } from '../components/hooks/useDocumentAllPages';
import useSplitDocument from '../components/hooks/useSplitDocument';
import useSetOrg from '../components/hooks/useSetOrg';
import { useSetTitle } from '../components/hooks/useSetTitle';
import { AppContext } from '../App';
import { ApiError } from '../api/types';
import { DocumentDetailsParamTypes } from './DocumentDetailsPage';
import {
  isDocumentAlreadySplit,
  splitCaution,
} from '../components/documents/split/splitEligibility';
import { PageReorderGrid } from '../components/documents/split/PageReorderGrid';
import { CoverPagesRow } from '../components/documents/split/CoverPagesRow';
import { PagePreviewDialog } from '../components/documents/split/PagePreviewDialog';
import {
  boundariesToPageGroups,
  formatChildrenSummary,
  periodicBoundaries,
} from '../components/documents/split/pageRanges';

// Shared height for every control in the header toolbar so the rotate-all
// group, "Split every…", the tile-size toggle, Cancel and Save all line up.
const CONTROL_HEIGHT = 36;

function arraysEqual(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/** Pages 1..n minus the given covers, preserving ascending PDF-page order. */
function mainIdentityOrder(n: number, covers: number[]): number[] {
  const cs = new Set(covers);
  const out: number[] = [];
  for (let p = 1; p <= n; p++) if (!cs.has(p)) out.push(p);
  return out;
}

/**
 * Document splitting / rotation page (legacy GlyntUI). Ported from
 * glynt-ui-next/src/routes/document-split.tsx. The operator carves the parent
 * PDF into child documents — choosing page groups, reordering, rotating,
 * designating cover pages, and omitting pages — then submits to the split
 * endpoint. This is a deliberately temporary feature in the legacy app.
 */
const DocumentSplitPage = () => {
  const { dpid, documentId } = useParams<DocumentDetailsParamTypes>();
  const navigate = useNavigate();
  const { setToast } = useContext(AppContext);
  const queryClient = useQueryClient();

  const { loading: orgLoading } = useSetOrg(dpid);

  const { data: manifest, isPending: docLoading } = usePaginatedDocumentsManifest({
    limit: 1,
    dpId: dpid!,
    defaultPage: 0,
    includeId: documentId,
  });
  const doc = manifest?.results[0];
  useSetTitle(`Split — ${doc?.label ?? ''}`);

  // Legacy page-image pattern: fetch every page once via the document's page
  // URLs and key them by 1-indexed PDF page number. No per-page image endpoint.
  const pageResults = useDocumentAllPages(doc?.pages, true);
  const imagesLoading = pageResults.some((r: any) => r.isLoading || r.isFetching);
  const pageImages = useMemo(() => {
    const map: Record<number, string | undefined> = {};
    pageResults.forEach((r: any, i: number) => {
      map[i + 1] = r.data?.file_temp_url;
    });
    return map;
  }, [pageResults]);

  const split = useSplitDocument(dpid!, documentId!);

  // Boundaries are 1-indexed VISIBLE positions where a new child starts.
  const [boundaries, setBoundaries] = useState<number[]>([]);
  // null = identity order; otherwise a staged reorder permutation.
  const [userOrder, setUserOrder] = useState<number[] | null>(null);
  const [periodicN, setPeriodicN] = useState<string>('');
  // Rotations keyed by ORIGINAL PDF page number so they survive reorder.
  const [rotations, setRotations] = useState<Record<number, 0 | 90 | 180 | 270>>({});
  // Omitted pages, dropped from every child. Keyed by ORIGINAL PDF page number.
  const [omittedPages, setOmittedPages] = useState<Set<number>>(new Set());
  // Cover pages prepended to every child in DESIGNATION ORDER (never sort).
  const [coverPages, setCoverPages] = useState<number[]>([]);
  const [previewPage, setPreviewPage] = useState<number | null>(null);
  const [tileSize, setTileSize] = useState<'default' | 'large'>('default');
  const [periodicAnchor, setPeriodicAnchor] = useState<HTMLElement | null>(null);

  const pageCount = doc?.pages?.length ?? 0;

  function toggleOmit(pdfPage: number) {
    // gp-025#15 guard: a cover page can't also be omitted.
    if (coverPages.includes(pdfPage)) return;
    setOmittedPages((prev) => {
      const next = new Set(prev);
      if (next.has(pdfPage)) next.delete(pdfPage);
      else next.add(pdfPage);
      return next;
    });
  }

  function designateCover(pdfPage: number) {
    if (omittedPages.has(pdfPage)) return;
    if (coverPages.includes(pdfPage)) return;
    setCoverPages((prev) => [...prev, pdfPage]);
    setUserOrder((prev) => (prev === null ? null : prev.filter((p) => p !== pdfPage)));
  }

  function unmarkCover(pdfPage: number) {
    setCoverPages((prev) => prev.filter((p) => p !== pdfPage));
    setUserOrder((prev) => (prev === null ? null : [...prev, pdfPage]));
  }

  function reorderCovers(next: number[]) {
    setCoverPages(next);
  }

  function rotatePage(pdfPage: number, delta: -90 | 90) {
    setRotations((prev) => {
      const current = prev[pdfPage] ?? 0;
      const next = ((((current + delta) % 360) + 360) % 360) as 0 | 90 | 180 | 270;
      const out = { ...prev };
      if (next === 0) delete out[pdfPage];
      else out[pdfPage] = next;
      return out;
    });
  }

  function rotateAllPages(delta: -90 | 90) {
    setRotations((prev) => {
      const out: Record<number, 0 | 90 | 180 | 270> = {};
      for (let p = 1; p <= pageCount; p++) {
        const current = prev[p] ?? 0;
        const next = ((((current + delta) % 360) + 360) % 360) as 0 | 90 | 180 | 270;
        if (next !== 0) out[p] = next;
      }
      return out;
    });
  }

  const mainPagesIdentity = useMemo(
    () => mainIdentityOrder(pageCount, coverPages),
    [pageCount, coverPages]
  );
  const pageOrder = userOrder ?? mainPagesIdentity;
  const reordered =
    userOrder !== null && pageOrder.length > 0 && !arraysEqual(pageOrder, mainPagesIdentity);

  const groups = useMemo(
    () => boundariesToPageGroups(boundaries, pageOrder),
    [boundaries, pageOrder]
  );
  // Child documents as the backend will actually create them: omitted pages
  // dropped, wholly-omitted children removed. Drives the header summary and
  // the submit payload.
  const effectiveGroups = useMemo(
    () =>
      groups
        .map((g) => g.filter((p) => !omittedPages.has(p)))
        .filter((g) => g.length > 0),
    [groups, omittedPages]
  );
  const hasRotations = Object.keys(rotations).length > 0;
  const hasOmissions = omittedPages.size > 0;
  const hasCovers = coverPages.length > 0;
  const isDirty =
    boundaries.length > 0 || reordered || hasRotations || hasOmissions || hasCovers;
  // The backend stays authoritative (active background jobs may not be visible
  // to the UI). We proactively block documents that already have split children
  // and ones whose data is EXCLUDED; a split child and a document still
  // processing (PENDING_CREATION) are both allowed through.
  const caution = doc ? splitCaution(doc) : null;
  // Re-splitting is allowed, so the operator must see what the previous split produced:
  // overlapping pages double-deliver, and no duplicate check compares page ranges.
  const existingSplitChildren = doc && isDocumentAlreadySplit(doc)
    ? doc.relationships?.split?.children ?? []
    : [];
  const canSplit = pageCount >= 1 && isDirty;

  const childrenSummary = formatChildrenSummary(effectiveGroups);

  // Set true right before a programmatic navigate that should bypass the
  // unsaved-changes guard (Split success).
  const bypassGuardRef = useRef(false);

  // Browser-level guard for tab close / refresh / external nav. The legacy app
  // uses <BrowserRouter> (not a data router), so React Router's useBlocker is
  // unavailable for in-app nav — the beforeunload guard covers the riskiest
  // cases, and Cancel surfaces an explicit discard confirmation.
  useEffect(() => {
    if (!isDirty) return;
    function onBeforeUnload(e: BeforeUnloadEvent) {
      if (bypassGuardRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const [discardOpen, setDiscardOpen] = useState(false);

  function toggleBoundary(visiblePosition: number) {
    setBoundaries((prev) =>
      prev.includes(visiblePosition)
        ? prev.filter((b) => b !== visiblePosition)
        : [...prev, visiblePosition].sort((a, b) => a - b)
    );
  }

  function applyPeriodic() {
    const n = Number(periodicN);
    const next = periodicBoundaries(n, pageCount);
    if (next.length === 0) {
      setToast({
        open: true,
        message: 'Pick a value between 1 and one less than the page count.',
        variant: 'error',
      });
      return;
    }
    setBoundaries(next);
    setPeriodicAnchor(null);
  }

  async function handleSubmit() {
    const rotationsPayload: Record<number, 90 | 180 | 270> = {};
    for (const [k, v] of Object.entries(rotations)) {
      const pdfPage = Number(k);
      if (v !== 0 && !omittedPages.has(pdfPage)) {
        rotationsPayload[pdfPage] = v as 90 | 180 | 270;
      }
    }
    const omittedArr = Array.from(omittedPages).sort((a, b) => a - b);
    try {
      await split.mutateAsync({
        page_groups: effectiveGroups,
        ...(Object.keys(rotationsPayload).length ? { page_rotations: rotationsPayload } : {}),
        ...(omittedArr.length ? { omitted_pages: omittedArr } : {}),
        // cover_pages preserves designation order (gp-025#14) — do not sort.
        ...(coverPages.length ? { cover_pages: coverPages } : {}),
      });
      // Splitting now runs asynchronously in boxcars: the endpoint returns 202
      // with no children (they're created in the background once the job
      // completes), so we can't report a count here. Invalidate the manifest so
      // the children appear on the list as they land.
      setToast({
        open: true,
        message: 'Split started · child documents will appear shortly',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      bypassGuardRef.current = true;
      navigate(`/data-pools/${dpid}/documents`);
    } catch (err) {
      const apiErr = err as ApiError;
      const message = err instanceof Error ? err.message : String(err);
      // error_code is preferred; fall back to scanning the message in case the
      // legacy endpoint surfaces the code only in the detail text.
      const has = (token: string) => apiErr?.errorCode === token || message.includes(token);

      // Kept only for a node older than glynt-api 6.187.0, which stopped raising this
      // (#1581); 6.187.0 and 6.187.1 both already omit it. Its replacement,
      // DUPLICATE_SPLIT_REQUEST, is deliberately NOT branched on: it is raised inside
      // save_split_children, after this request has already returned 202, so it can never
      // reach this catch. The synchronous refusals (NOT_A_PDF, NO_PARENT_DATA and the
      // page-validation 400s) fall to the else, where getAxiosError has already surfaced
      // the server's own `detail` — a per-code branch here would just restate it.
      if (has('ALREADY_SPLIT')) {
        // Stay on the page rather than navigating: leaving unmounts the staged boundaries,
        // rotations, omissions and cover pages.
        setToast({
          open: true,
          message: 'This document can no longer be split.',
          variant: 'warning',
        });
        queryClient.invalidateQueries({ queryKey: ['documents'] });
      } else {
        setToast({
          open: true,
          message: `Failed to split document: ${message}`,
          variant: 'error',
        });
      }
    }
  }

  function handleCancel() {
    if (isDirty) setDiscardOpen(true);
  }

  function confirmDiscardCancel() {
    setBoundaries([]);
    setUserOrder(null);
    setRotations({});
    setOmittedPages(new Set());
    setCoverPages([]);
    setPeriodicN('');
    setDiscardOpen(false);
  }

  if (orgLoading || docLoading) {
    return (
      <>
        <TopBar />
        <LinearProgress />
      </>
    );
  }

  if (!doc) {
    return (
      <>
        <TopBar />
        <Box sx={{ p: 3 }}>
          <Typography>Document not found.</Typography>
        </Box>
      </>
    );
  }

  // Derive the preview dialog's boundary/child context for the active page.
  const previewExtras = (() => {
    if (previewPage === null) return {};
    if (coverPages.includes(previewPage)) return { isLastVisible: true };
    const visibleIdx = pageOrder.indexOf(previewPage);
    if (visibleIdx < 0) return {};
    const nextPosition = visibleIdx + 2;
    const isLast = visibleIdx === pageOrder.length - 1;
    let ci = 1;
    for (let i = 0; i < visibleIdx; i++) {
      if (boundaries.includes(i + 2)) ci += 1;
    }
    return {
      isLastVisible: isLast,
      boundaryAfterActive: boundaries.includes(nextPosition),
      onToggleBoundaryAfter: () => toggleBoundary(nextPosition),
      childIndex: ci,
      childCount: groups.length,
    };
  })();

  return (
    <>
      <TopBar />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          height: 'calc(100vh - 64px)',
          p: 2,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              Split / Rotate: {doc.label}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {pageCount} page{pageCount === 1 ? '' : 's'} · {effectiveGroups.length} child document
              {effectiveGroups.length === 1 ? '' : 's'}
              {childrenSummary ? (
                <>
                  {' · '}
                  <span data-cy="children-summary">{childrenSummary}</span>
                </>
              ) : null}
              {reordered ? ' · reordered' : ''}
              {hasCovers ? ` · ${coverPages.length} cover` : ''}
              {hasOmissions ? ` · ${omittedPages.size} omitted` : ''}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            {/* Rotate all */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 0.5,
                height: CONTROL_HEIGHT,
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
                Rotate all
              </Typography>
              <Tooltip title="Rotate every page left (90° counter-clockwise)">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => rotateAllPages(-90)}
                    disabled={pageCount === 0}
                    aria-label="Rotate every page left"
                    data-cy="rotate-all-left"
                  >
                    <RotateLeftIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Rotate every page right (90° clockwise)">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => rotateAllPages(90)}
                    disabled={pageCount === 0}
                    aria-label="Rotate every page right"
                    data-cy="rotate-all-right"
                  >
                    <RotateRightIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            {/* Periodic split */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCutIcon sx={{ fontSize: 16 }} />}
              disabled={pageCount < 2}
              data-cy="periodic-trigger"
              onClick={(e) => setPeriodicAnchor(e.currentTarget)}
              sx={{ height: CONTROL_HEIGHT }}
            >
              Split every…
            </Button>
            <Popover
              open={Boolean(periodicAnchor)}
              anchorEl={periodicAnchor}
              onClose={() => setPeriodicAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Box
                component="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  applyPeriodic();
                }}
                sx={{ p: 2, width: 260, display: 'flex', flexDirection: 'column', gap: 1 }}
              >
                <Typography variant="subtitle2">Periodic split</Typography>
                <Typography variant="caption" color="text.secondary">
                  Insert a boundary at every Nth page across the whole document.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Every
                  </Typography>
                  <TextField
                    type="number"
                    size="small"
                    autoFocus
                    value={periodicN}
                    onChange={(e) => setPeriodicN(e.target.value)}
                    placeholder="N"
                    inputProps={{ min: 1, max: Math.max(1, pageCount - 1) }}
                    sx={{ width: 80 }}
                    data-cy="periodic-input"
                  />
                  <Typography variant="caption" color="text.secondary">
                    pages
                  </Typography>
                  <Button
                    type="submit"
                    size="small"
                    variant="outlined"
                    sx={{ ml: 'auto' }}
                    disabled={!periodicN.trim()}
                    data-cy="periodic-apply"
                  >
                    Apply
                  </Button>
                </Box>
              </Box>
            </Popover>

            {/* Tile size */}
            <ToggleButtonGroup
              size="small"
              exclusive
              value={tileSize}
              onChange={(_e, v) => v && setTileSize(v)}
              aria-label="Thumbnail size"
              sx={{ height: CONTROL_HEIGHT }}
            >
              <ToggleButton value="default" aria-label="Compact thumbnails" data-cy="tile-size-default">
                <ViewModuleIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="large" aria-label="Larger thumbnails" data-cy="tile-size-large">
                <ViewComfyIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>

            <Button
              size="small"
              onClick={handleCancel}
              disabled={split.isPending || !isDirty}
              data-cy="split-cancel"
              sx={{ height: CONTROL_HEIGHT }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
              disabled={!canSplit || split.isPending}
              data-cy="split-save"
              sx={{ height: CONTROL_HEIGHT }}
            >
              {split.isPending ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </Box>

        {/* Body */}
        <Box sx={{ minHeight: 0, flex: 1, overflowY: 'auto', pb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {caution && (
            <Alert severity="warning" data-cy="split-caution-banner">
              {caution}
            </Alert>
          )}
          {existingSplitChildren.length > 0 && (
            <Alert severity="warning" data-cy="split-already-split-banner">
              This document was already split into {existingSplitChildren.length} child
              document{existingSplitChildren.length === 1 ? '' : 's'}. Splitting again
              creates an additional set — if a page you include here is also covered by a
              child that is still being delivered, that page will be delivered twice.
            </Alert>
          )}
          <CoverPagesRow
            coverPages={coverPages}
            onReorder={reorderCovers}
            pageImages={pageImages}
            imagesLoading={imagesLoading}
            rotations={rotations}
            onRotate={rotatePage}
            onView={setPreviewPage}
            onUnmark={unmarkCover}
          />
          <PageReorderGrid
            pageOrder={pageOrder}
            onReorder={setUserOrder}
            pageImages={pageImages}
            imagesLoading={imagesLoading}
            rotations={rotations}
            onRotate={rotatePage}
            boundaries={boundaries}
            onToggleBoundary={toggleBoundary}
            onView={setPreviewPage}
            omittedPages={omittedPages}
            onToggleOmit={toggleOmit}
            onDesignateCover={designateCover}
            tileSize={tileSize}
            slotIndexOffset={coverPages.length}
          />
        </Box>
      </Box>

      {/* Cancel discard dialog */}
      <ConfirmDialog
        open={discardOpen}
        title="Discard unsaved changes?"
        description="This will clear all in-progress boundaries, reorder, and rotations. You will stay on this page."
        confirmLabel="Discard changes"
        onClose={() => setDiscardOpen(false)}
        onConfirm={confirmDiscardCancel}
      />

      <PagePreviewDialog
        pageNumber={previewPage}
        pageCount={pageCount}
        pageImages={pageImages}
        imagesLoading={imagesLoading}
        rotation={previewPage ? rotations[previewPage] ?? 0 : 0}
        onRotate={(delta) => previewPage && rotatePage(previewPage, delta)}
        onNavigate={setPreviewPage}
        onClose={() => setPreviewPage(null)}
        omitted={previewPage !== null && omittedPages.has(previewPage)}
        onToggleOmit={previewPage !== null ? () => toggleOmit(previewPage) : undefined}
        isCover={previewPage !== null && coverPages.includes(previewPage)}
        onToggleCover={
          previewPage !== null
            ? () =>
                coverPages.includes(previewPage)
                  ? unmarkCover(previewPage)
                  : designateCover(previewPage)
            : undefined
        }
        {...previewExtras}
      />
    </>
  );
};

function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{description}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-cy="discard-cancel">
          Keep editing
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={() => {
            onConfirm();
          }}
          data-cy="discard-confirm"
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DocumentSplitPage;
